import React, { ComponentType, useEffect, useRef, useState } from 'react';
import classNames from 'classnames';

import { track } from 'lib/analytics';

import FilterControls from './FilterControls';
import FilterIcon from './FilterIcon';
import SortControls from './SortControls';
import { CELL_PADDING } from './constants';
import {
  ColumnWidthProps,
  Direction,
  FilterState,
  Filters,
  HeaderWidthProps,
  Row,
  SetFilter,
  SetSortState
} from './types';
import Layout from '../Layout';
import Text from '../Text';
import { useKeyPress } from '../helpers';

interface Props<T extends Row> extends HeaderWidthProps, ColumnWidthProps {
  columnKey: string;
  value: string;
  activeSortDirection?: Direction;
  sortable: boolean;
  setSortState: SetSortState;
  filter?: FilterState;
  setFilter: SetFilter;
  filters: Filters<T>;
  alignRight: boolean;
  CustomHeader?: ComponentType;
  isMoney?: boolean;
  isFirstColumn?: boolean;
}

const Header = <T extends Row>({
  value,
  sortable,
  activeSortDirection,
  setSortState,
  filter,
  setFilter,
  filters,
  setColumnWidths,
  getColumnWidth,
  columnKey,
  shrinkWidth,
  alignRight,
  flexGrow,
  width,
  minWidth,
  maxWidth,
  CustomHeader,
  isMoney,
  isFirstColumn
}: Props<T>) => {
  const [searchInputOpen, setSearchInputOpen] = useState<boolean>(false);
  const [filterInputOpen, setFilterInputOpen] = useState<boolean>(false);
  const [dateRangeOpen, setDateRangeOpen] = useState<boolean>(false);
  const [moneyRangeOpen, setMoneyRangeOpen] = useState<boolean>(false);
  const [numberRangeOpen, setNumberRangeOpen] = useState<boolean>(false);
  const headerRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const measuredWidth = getColumnWidth(columnKey);
  const isMeasured = measuredWidth !== undefined;
  const alignHeaderRight = isMoney && !isFirstColumn;

  useKeyPress((e: KeyboardEvent) => {
    if (e.key !== 'Escape') {
      return;
    }
    if (searchInputOpen) {
      setSearchInputOpen(false);
    }
    if (filterInputOpen) {
      setFilterInputOpen(false);
    }
    if (dateRangeOpen) {
      setDateRangeOpen(false);
    }
    if (numberRangeOpen) {
      setNumberRangeOpen(false);
    }
    if (moneyRangeOpen) {
      setMoneyRangeOpen(false);
    }
  });

  const hideFilterInputs = () => {
    setSearchInputOpen(false);
    setFilterInputOpen(false);
    setDateRangeOpen(false);
    setNumberRangeOpen(false);
    setMoneyRangeOpen(false);
  };

  useEffect(() => {
    if (!headerRef.current) {
      return;
    }

    const clientWidth = headerRef.current.clientWidth;

    if (measuredWidth === clientWidth) {
      return;
    }

    setColumnWidths({ [columnKey]: clientWidth });
  }, [columnKey, setColumnWidths, isMeasured]); // eslint-disable-line react-hooks/exhaustive-deps

  const contentWidth = contentRef.current?.getBoundingClientRect().width;
  const isAsc = activeSortDirection === 'asc';

  return (
    <div
      className={classNames('Table-header', { 'money-header': alignHeaderRight })}
      ref={headerRef}
      style={{
        width: measuredWidth || width,
        minWidth,
        maxWidth,
        alignItems: 'center',
        position: 'relative',
        flexGrow: isMeasured || width ? undefined : shrinkWidth ? 0 : flexGrow || 1,
        flexShrink: isMeasured || width ? undefined : flexGrow !== undefined ? 0 : 1,
        paddingRight: alignHeaderRight ? undefined : CELL_PADDING,
        display: 'flex'
      }}
    >
      <div
        ref={contentRef}
        role="button"
        style={{
          cursor: sortable ? 'pointer' : undefined,
          display: 'flex',
          alignItems: 'center',
          width: '100%',
          paddingLeft: CELL_PADDING,
          paddingTop: CELL_PADDING,
          paddingBottom: CELL_PADDING
        }}
        onClick={() => {
          if (!sortable) return;

          if (!activeSortDirection) {
            track('Element Interacted', { type: 'iconButton', name: 'table sort desc' });
            setSortState('desc');
          } else if (isAsc) {
            track('Element Interacted', { type: 'iconButton', name: 'table sort asc' });
            setSortState(null);
          } else {
            track('Element Interacted', { type: 'iconButton', name: 'table sort asc' });
            setSortState('asc');
          }
        }}
      >
        {CustomHeader ? (
          <CustomHeader />
        ) : (
          <>
            {isMoney ? (
              <div
                style={{
                  textAlign: 'right'
                }}
              >
                <Text
                  size="body3"
                  color="gray-700"
                  semibold
                  wrap={false}
                  inline
                  align="right"
                  __style={{ textAlign: 'right' }}
                >
                  {value}
                </Text>
              </div>
            ) : (
              <div
                style={{
                  flexGrow: 1,
                  display: 'flex',
                  justifyContent: 'flex-start',
                  width: '100%',
                  textAlign: 'left'
                }}
              >
                <Text
                  size="body3"
                  color="gray-700"
                  semibold
                  wrap={false}
                  inline
                  align="left"
                  __style={{ textAlign: 'left' }}
                >
                  {value}
                </Text>
              </div>
            )}
          </>
        )}
        <Layout display="inline-flex">
          <SortControls sortable={sortable} activeSortDirection={activeSortDirection} />
        </Layout>
      </div>
      {!isMoney && (
        <Layout align="center">
          <FilterIcon
            hideFilterInputs={hideFilterInputs}
            // @ts-expect-error
            filters={filters}
            filter={filter}
            searchInputOpen={searchInputOpen}
            setSearchInputOpen={setSearchInputOpen}
            filterInputOpen={filterInputOpen}
            setFilterInputOpen={setFilterInputOpen}
            dateRangeOpen={dateRangeOpen}
            setDateRangeOpen={setDateRangeOpen}
            moneyRangeOpen={moneyRangeOpen}
            setMoneyRangeOpen={setMoneyRangeOpen}
            numberRangeOpen={numberRangeOpen}
            setNumberRangeOpen={setNumberRangeOpen}
          />
          {contentWidth ? (
            <FilterControls
              hideFilterInputs={hideFilterInputs}
              headerWidth={contentWidth}
              searchInputOpen={searchInputOpen}
              filterInputOpen={filterInputOpen}
              dateRangeOpen={dateRangeOpen}
              moneyRangeOpen={moneyRangeOpen}
              numberRangeOpen={numberRangeOpen}
              filters={filters}
              filter={filter}
              setFilter={setFilter}
              alignRight={alignRight}
            />
          ) : null}
        </Layout>
      )}
    </div>
  );
};

export default Header;
